import { useState } from 'react';
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	GripVertical,
	Edit,
	Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AccordionProps {
	questions: Questions.Question[];
	defaultOpenId?: string | null;
	className?: string;
	variant?: 'default' | 'landing';
	itemsPerPage?: number;
	showHandles?: boolean;
	onEdit?: (question: Questions.Question) => void;
	onDelete?: (question: Questions.Question) => void;
	onReorder?: (question: Questions.Question) => void;
}

const Accordion = ({
	questions,
	defaultOpenId = null,
	className = '',
	variant = 'default',
	itemsPerPage = 5,
	showHandles = false,
	onEdit,
	onDelete,
	onReorder,
}: AccordionProps) => {
	const isLandingVariant = variant === 'landing';
	const [openId, setOpenId] = useState<string | null>(defaultOpenId);
	const [currentPage, setCurrentPage] = useState(0);

	const toggleQuestion = (id: string) => {
		setOpenId(openId === id ? null : id);
	};

	const totalPages = Math.ceil(questions.length / itemsPerPage);
	const startIndex = currentPage * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const visibleQuestions = questions
		.sort((a, b) => a.priority - b.priority)
		.slice(startIndex, endIndex);

	const nextPage = () =>
		setCurrentPage((prev) => (prev === totalPages - 1 ? 0 : prev + 1));
	const prevPage = () =>
		setCurrentPage((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
	const goToPage = (page: number) => setCurrentPage(page);

	const handleAction = (e: React.MouseEvent, action: () => void) => {
		e.stopPropagation();
		action();
	};

	return (
		<div
			className={cn(
				'mx-auto w-full max-w-4xl px-4',
				isLandingVariant && 'font-albert',
				className,
			)}
		>
			<div className="mb-10">
				{visibleQuestions.map((question) => {
					const isOpen = openId === question.id;

					return (
						<div
							key={question.id}
							className={cn(
								'group relative border-b border-zinc-100 last:border-0',
								isLandingVariant && 'border-[#e4e4e0]',
							)}
						>
							{/* Handles de Administração (Mantidos para funcionalidade Admin) */}
							{showHandles && (
								<div className="absolute right-12 top-6 z-30 flex items-center gap-1">
									{onReorder && (
										<button
											className="rounded p-1.5 hover:bg-zinc-100"
											onClick={(e) =>
												handleAction(e, () =>
													onReorder(question),
												)
											}
										>
											<GripVertical className="h-4 w-4 text-zinc-400" />
										</button>
									)}
									{onEdit && (
										<button
											className="rounded p-1.5 hover:bg-zinc-100"
											onClick={(e) =>
												handleAction(e, () =>
													onEdit(question),
												)
											}
										>
											<Edit className="h-4 w-4 text-zinc-400" />
										</button>
									)}
									{onDelete && (
										<button
											className="rounded p-1.5 hover:bg-red-50 hover:text-red-600"
											onClick={(e) =>
												handleAction(e, () =>
													onDelete(question),
												)
											}
										>
											<Trash2 className="h-4 w-4" />
										</button>
									)}
								</div>
							)}

							{/* Botão da Pergunta */}
							<button
								className={cn(
									'group/btn flex w-full items-center justify-between py-7 text-left transition-all focus:outline-none',
									isLandingVariant && 'py-6',
								)}
								onClick={() => toggleQuestion(question.id)}
								aria-expanded={isOpen}
							>
								<span
									className={cn(
										'text-base font-bold tracking-tight transition-colors duration-300 md:text-lg',
										isLandingVariant &&
											'font-libre text-[22px] font-bold leading-[1.25] tracking-[-0.01em] text-[#1c1c1c] md:text-[24px]',
									)}
								>
									{question.question}
								</span>

								{/* Wrapper do ícone estilo círculo */}
								<div
									className={cn(
										'ml-4 flex size-8 shrink-0 items-center justify-center rounded-full transition-all duration-300',
										isOpen
											? 'bg-zinc-700 text-white shadow-lg shadow-zinc-200'
											: 'bg-zinc-50 text-zinc-400 group-hover/btn:bg-zinc-100',
										isLandingVariant &&
											(isOpen
												? 'bg-brand-orange text-white shadow-[0_10px_20px_rgba(255,113,70,0.2)]'
												: 'bg-[#f5f5f2] text-[#696969] group-hover/btn:bg-[#ecece8]'),
									)}
								>
									<ChevronDown
										className={cn(
											'size-4 transition-transform duration-300',
											isOpen ? 'rotate-180' : 'rotate-0',
										)}
									/>
								</div>
							</button>

							{/* Resposta */}
							<div
								className={cn(
									'overflow-hidden transition-all duration-300 ease-in-out',
									isOpen ? 'max-h-[500px] pb-8' : 'max-h-0',
									isLandingVariant && (isOpen ? 'pb-7' : ''),
								)}
							>
								<div
									className={cn(
										'max-w-[90%] text-start text-sm leading-relaxed text-zinc-400 md:text-base',
										isLandingVariant &&
											'font-albert max-w-[95%] text-[15px] leading-[1.7] text-[#696969]',
									)}
								>
									{question.answer}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* Paginação */}
			{questions.length > itemsPerPage && (
				<div className="flex items-center justify-center gap-6 pt-4">
					<button
						onClick={prevPage}
						className={cn(
							'rounded-full p-2 text-zinc-900 transition-colors hover:bg-zinc-100',
							isLandingVariant &&
								'text-[#1c1c1c] hover:bg-[#f5f5f2]',
						)}
					>
						<ChevronLeft className="h-5 w-5" />
					</button>
					<div className="flex justify-center space-x-2.5">
						{Array.from({ length: totalPages }).map((_, index) => (
							<button
								key={index}
								onClick={() => goToPage(index)}
								className={cn(
									'h-2 w-2 rounded-full transition-all duration-300',
									currentPage === index
										? 'w-6 bg-zinc-900'
										: 'bg-zinc-200 hover:bg-zinc-300',
									isLandingVariant &&
										(currentPage === index
											? 'w-6 bg-brand-orange'
											: 'bg-[#d7d7d2] hover:bg-[#c9c9c2]'),
								)}
							/>
						))}
					</div>
					<button
						onClick={nextPage}
						className={cn(
							'rounded-full p-2 text-zinc-900 transition-colors hover:bg-zinc-100',
							isLandingVariant &&
								'text-[#1c1c1c] hover:bg-[#f5f5f2]',
						)}
					>
						<ChevronRight className="h-5 w-5" />
					</button>
				</div>
			)}
		</div>
	);
};

export { Accordion };
